import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Truck, RefreshCw, LogOut, Search, Home, ScanLine, AlertTriangle } from "lucide-react";
import { Escaner } from "@/components/empaque/Escaner";
import { ColocacionCard, ColocacionDetalle } from "@/components/colocacion/ColocacionCard";
import { PaqueteCapsula, PaqueteSinGuia } from "@/components/colocacion/PaqueteCapsula";
import { PaqueteCard, ElegirPedido, ResumenAgencias } from "@/components/colocacion/PaqueteCard";
import { CerrarColocacion, CierresColocacion } from "@/components/colocacion/CierreColocacion";
import { Felicidades, felMsg } from "@/components/Felicidades";
import { inicioDe } from "@/lib/inicio";

const TABS = [
  ["por_trasladar", "Por trasladar", "bg-amber-400 text-black border-amber-400", "por_trasladar"],
  ["trasladando", "Trasladando", "bg-zinc-900 text-white border-zinc-900", "trasladando"],
  ["colocados", "Colocados", "bg-emerald-600 text-white border-emerald-600", "colocados"],
  ["cierres", "Cierres", "bg-[#002FA7] text-white border-[#002FA7]", null],
];

const Contadores = ({ c, tab }) => (
  <div className="grid grid-cols-3 gap-2 mb-3">
    {TABS.slice(0, 3).map(([v, l, cl, k]) => (
      <div key={v} aria-disabled="true" className={`rounded-lg ${cl} p-2 text-center border-2 cursor-default select-none ${tab === v ? "ring-4 ring-offset-1 ring-zinc-400" : ""}`} data-testid={`col-cnt-${v}`}>
        <div className="text-[10px] uppercase font-bold opacity-90">{l}</div>
        <div className="text-3xl font-black tabular-nums">{k === "por_trasladar" ? c.pedidos_por_trasladar ?? 0 : c[k] ?? 0}</div>
      </div>
    ))}
  </div>
);

const Agencias = ({ lista, value, onChange, isAdmin, onEdit }) => (
  <div className="flex gap-2 mb-2">
    <select value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 h-14 rounded-lg border-2 border-zinc-900 px-3 text-xl font-bold bg-white" data-testid="col-agencia">
      {lista.map((a) => <option key={a} value={a}>{a}</option>)}
    </select>
    {isAdmin && <button type="button" onClick={onEdit} className="px-3 rounded-lg border-2 border-zinc-300 text-sm font-bold" data-testid="col-agencias-editar">Editar</button>}
  </div>
);

export default function AppColocacion() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [tab, setTab] = useState("por_trasladar");
  const [periodo, setPeriodo] = useState("actual");
  const [data, setData] = useState({ items: [], contadores: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [agencia, setAgencia] = useState(() => localStorage.getItem("col_agencia") || "Cargo Expreso");
  const [scan, setScan] = useState(false);
  const [sel, setSel] = useState(null);
  const [elegir, setElegir] = useState(null);
  const [fel, setFel] = useState(null);
  const [ult, setUlt] = useState(null);
  const [agencias, setAgencias] = useState(["Cargo Expreso"]);
  useEffect(() => { api.get("/colocacion/agencias").then(({ data }) => { setAgencias(data); if (!data.includes(agencia)) setAgencia(data[0]); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const elegirAgencia = (a) => { setAgencia(a); localStorage.setItem("col_agencia", a); };
  const editarAgencias = async () => {
    const t = window.prompt("Agencias (separadas por coma):", agencias.join(", "));
    if (t === null) return;
    try { const { data } = await api.put("/colocacion/agencias", { agencias: t.split(",") }); setAgencias(data); if (!data.includes(agencia)) elegirAgencia(data[0]); toast.success("Agencias guardadas"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const load = useCallback(async () => {
    if (tab === "cierres") { setLoading(false); return; }
    try { const { data: d } = await api.get("/colocacion/tablero", { params: { vista: tab, periodo } }); setData({ ...d, vista: tab }); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [tab, periodo]);
  useEffect(() => { setLoading(true); load(); const t = setInterval(() => { if (!document.hidden) load(); }, 20000); return () => clearInterval(t); }, [load]);

  const abrir = async (oid) => {
    try { const { data: d } = await api.get(`/colocacion/pedido/${oid}`); setSel(d); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const modo = tab === "trasladando" ? "colocar" : "trasladar";

  const enviar = async (codigo) => {
    try {
      const { data: r } = await api.post("/colocacion/escanear", { codigo, modo, agencia });
      if (!scan) toast.success(`${r.pedido} · guía ${r.guia}: ${r.estado_lbl.toUpperCase()}`);
      setUlt({ ok: true, txt: `${r.pedido} · guía ${r.guia}: ${r.estado_lbl.toUpperCase()}` });
      setFel(modo === "colocar"
        ? felMsg(r.pendientes, "colocar", "paquete", "paquetes", "COLOCACIÓN COMPLETA")
        : felMsg(r.pendientes, "recolectar", "paquete", "paquetes", "RECOLECCIÓN COMPLETA"));
      if (navigator.vibrate) navigator.vibrate(120);
      load();
    } catch (e) {
      if (navigator.vibrate) navigator.vibrate([80, 60, 80]);
      setUlt({ ok: false, txt: formatApiError(e) });
      if (e.response?.status === 404 && modo === "trasladar") {
        setScan(false);
        const [a, b] = await Promise.all(["actual", "anteriores"].map((p) => api.get("/colocacion/tablero", { params: { vista: "por_trasladar", periodo: p } })));
        return setElegir({ codigo, items: [...a.data.items, ...b.data.items].filter((p) => p.empacado) });
      }
      toast.error(formatApiError(e));
    }
  };
  const vincular = async (p) => {
    const codigo = elegir.codigo;
    setElegir(null);
    try { await api.post("/colocacion/vincular", { codigo, order_id: p.id }); await enviar(codigo); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const s = q.trim().toLowerCase();
  const items = (data.vista === tab ? data.items : []).filter((p) => !s || [p.numero, p.pedido, p.cliente, p.guia, ...(p.paquetes || []).map((g) => g.guia)].some((v) => (v || "").toLowerCase().includes(s)));
  const c = data.contadores || {};

  return (
    <div className="min-h-screen bg-white text-black" data-testid="app-colocacion">
      <header className="sticky top-0 z-40 bg-black text-white shadow-md">
        <div className="px-4 h-16 flex items-center justify-between">
          {sel ? <button onClick={() => setSel(null)} className="flex items-center gap-1 font-bold text-lg" data-testid="col-back"><ArrowLeft className="w-6 h-6" /> Volver</button>
            : <div className="flex items-center gap-2 font-black text-xl tracking-tight"><Truck className="w-7 h-7" /> COLOCACIÓN</div>}
          <div className="flex items-center gap-1">
            <button onClick={() => nav(inicioDe(user))} className="p-2" aria-label="Inicio" data-testid="col-home"><Home className="w-6 h-6" /></button>
            <button onClick={load} className="p-2" aria-label="Actualizar" data-testid="col-refresh"><RefreshCw className="w-6 h-6" /></button>
            <button onClick={logout} className="p-2" aria-label="Salir" data-testid="col-logout"><LogOut className="w-6 h-6" /></button>
          </div>
        </div>
        {!sel && <div className="px-4 pb-2 text-sm"><span className="opacity-80">Hola,</span> <b>{user?.name || user?.email}</b></div>}
      </header>
      <main className="px-3 pt-3 pb-28 max-w-3xl mx-auto">
        {sel ? <ColocacionDetalle p={sel} onReload={() => abrir(sel.id)} /> : (
          <>
            <Contadores c={c} tab={tab} />
            <div className="text-xs text-zinc-600 mb-2" data-testid="col-cnt-hoy">Hoy: {c.trasladados_hoy ?? 0} trasladados · {c.colocados_hoy ?? 0} colocados · {c.por_trasladar ?? 0} paquete(s) listos · {c.sin_empacar ?? 0} pedido(s) sin empacar</div>
            {c.atrasados_24h > 0 && <button onClick={() => setTab("trasladando")} className="w-full mb-2 rounded-lg bg-red-600 text-white p-2 text-sm font-bold flex items-center gap-2" data-testid="col-alerta-24h"><AlertTriangle className="w-5 h-5" /> {c.atrasados_24h} paquete(s) con más de 24 horas en Trasladando</button>}
            {c.anteriores_pendientes > 0 && <button onClick={() => { setTab("por_trasladar"); setPeriodo("anteriores"); }} className="w-full mb-2 rounded-lg bg-red-600 text-white p-2 text-sm font-bold flex items-center gap-2" data-testid="col-alerta-anteriores"><AlertTriangle className="w-5 h-5" /> {c.anteriores_pendientes} pedido(s) de períodos anteriores sin trasladar</button>}
            <div className="grid grid-cols-4 gap-1 mb-3">
              {TABS.map(([v, l, cl]) => <button key={v} onClick={() => setTab(v)} className={`h-12 rounded-lg font-black uppercase text-[10px] sm:text-sm px-0.5 border-2 ${tab === v ? cl : "bg-white border-zinc-300"}`} data-testid={`col-tab-${v}`}>{l}</button>)}
            </div>
            {tab === "cierres" ? <CierresColocacion /> : (
              <>
                {tab === "trasladando" && <Agencias lista={agencias} value={agencia} onChange={elegirAgencia} isAdmin={isAdmin} onEdit={editarAgencias} />}
                {tab !== "colocados" && <button onClick={() => { setUlt(null); setFel(null); setScan(true); }} className={`w-full h-16 mb-3 rounded-lg ${tab === "trasladando" ? "bg-emerald-600 text-white" : "bg-zinc-900 text-white"} text-xl font-black uppercase flex items-center justify-center gap-2`} data-testid="col-scan"><ScanLine className="w-7 h-7" /> {tab === "trasladando" ? "Escanear · Colocado" : "Escanear · Trasladando"}</button>}
                {tab === "colocados" && <><ResumenAgencias rows={data.por_agencia || []} /><CerrarColocacion cont={c} agencias={agencias} onDone={load} /></>}
                {tab === "por_trasladar" && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {[["actual", "Período actual"], ["anteriores", "Períodos anteriores"]].map(([v, l]) => <button key={v} onClick={() => setPeriodo(v)} className={`h-10 rounded-lg text-sm font-bold border-2 ${periodo === v ? "bg-amber-400 border-amber-400" : "border-zinc-300"}`} data-testid={`col-periodo-${v}`}>{l}</button>)}
                  </div>
                )}
                <div className="relative mb-3"><Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pedido, cliente o guía" className="w-full h-14 rounded-lg border-2 border-zinc-300 pl-11 pr-3 text-lg" data-testid="col-buscar" /></div>
                {loading || data.vista !== tab ? <div className="text-center py-16 text-zinc-500 text-lg">Cargando…</div>
                  : items.length === 0 ? <div className="text-center py-20 text-zinc-600 font-bold text-lg" data-testid="col-vacio">Sin registros</div>
                  : <div className="space-y-3">{tab === "por_trasladar"
                    ? items.flatMap((p) => {
                      const qs = p.paquetes.filter((q) => !["trasladando", "colocado", "recolectado"].includes(q.estado)).map((q) => ({ ...q, sg: false }));
                      const all = [...qs, ...(p.paquetes_sin_guia || []).map((q) => ({ ...q, sg: true }))].sort((a, b) => (a.num || 99) - (b.num || 99));
                      return all.length ? all.map((q) => (q.sg ? <PaqueteSinGuia key={`sg-${p.id}-${q.n}`} r={p} q={q} onOpen={() => abrir(p.id)} />
                        : <PaqueteCapsula key={q.guia} r={p} q={q} onOpen={() => abrir(p.id)} onChanged={load} />)) : [<ColocacionCard key={p.id} p={p} onOpen={() => abrir(p.id)} />];
                    })
                    : items.map((p) => <PaqueteCard key={p.guia} p={p} onOpen={() => abrir(p.order_id)} onChanged={load} />)}</div>}
              </>
            )}
          </>
        )}
      </main>
      {scan && (
        <Escaner titulo={modo === "colocar" ? "COLOCAR PAQUETES" : "RECOLECTAR PAQUETES"} continuo onCode={enviar} onClose={() => setScan(false)}>
          {ult && <div className={`p-3 text-base font-bold ${ult.ok ? "bg-emerald-600" : "bg-red-700"}`} data-testid="col-scan-resultado">{ult.txt}</div>}
        </Escaner>
      )}
      <Felicidades msg={fel} />
      {elegir && <ElegirPedido codigo={elegir.codigo} items={elegir.items} onClose={() => setElegir(null)} onPick={vincular} />}
    </div>
  );
}
